import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import * as Icon from 'react-feather';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { Button } from '@squirrellearningltd/go-teacher-components';
import styles from './BookSearch.module.scss';
import { useTranslation } from 'react-i18next';

import IsbnSearch from './components/isbnSearch';
import BookRecommendationItem from './components/BookRecommendationItem';
import ReadingSchemeCarousel from './components/ReadingSchemeCarousel';
import { ASSIGN_READING_BOOK, BOOK_SEARCH_QUERY, GET_PUBLISHERS_QUERY, GET_RECOMMENDED_BOOKS } from './services/graphql';
import { GET_STUDENT_QUERY } from '../../scenes/Student/services/graphql';
import { BOOK_TYPES, READING_BOOK_TYPE } from './services/constants';
import coverUnavailableGreen from '../../images/cover-unavaliable-green.png';
import coverUnavailablePurple from '../../images/cover-unavaliable-purple.png';

const fallbackCovers = [coverUnavailableGreen, coverUnavailablePurple];

const BookRecommendationSearch = ({ student, studentId, close, reset, showRecommendedBooks }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [showIsbnSearch, setShowIsbnSearch] = useState(false);
    const [selectedScheme, setSelectedScheme] = useState(null);
    const [selectedBook, setSelectedBook] = useState({ open: false, book: null });
    const [readingBook, setReadingBook] = useState({
        bookType: null,
        hideComments: true
    });
    const studentName = student && student.name ? student.name : 'this pupil';
    const bookTypes = BOOK_TYPES.filter(bookType => (bookType.type !== READING_BOOK_TYPE.group && bookType.type !== 'NewGroup'));
    const selectedSchemeId = selectedScheme ? selectedScheme.id : null;
    const { data: publishersData, loading: publishersLoading, error: publishersError } = useQuery(GET_PUBLISHERS_QUERY);
    const { data: recommendedData, loading: recommendedLoading, error: recommendedError } = useQuery(GET_RECOMMENDED_BOOKS, {
        variables: {
            studentId: studentId,
        },
        skip: !showRecommendedBooks
    });
    const recommendedBooks = recommendedData && recommendedData.studentRecommendedBooks ? recommendedData.studentRecommendedBooks.slice(0, 6) : [];
    const readingSchemes = publishersData && publishersData.publishers ?
        publishersData.publishers.map((publisher, index) => ({
            ...publisher,
            iconUrl: fallbackCovers[index % fallbackCovers.length]
        })) :
        [];

    const [assignReadingBook] = useMutation(ASSIGN_READING_BOOK, {
        awaitRefetchQueries: true,
        onCompleted: () => {
            close && close(false);
            reset && reset();
        },
        refetchQueries: () => [
            {
                query: GET_STUDENT_QUERY,
                variables: {
                    id: studentId,
                }
            }
        ]
    });

    const [searchBooks, { data: searchData, loading: searchLoading, error: searchError }] = useLazyQuery(BOOK_SEARCH_QUERY, {
        fetchPolicy: 'network-only',
    });
    const searchResults = searchData && searchData.booksByTitleAuthorIsbn ? searchData.booksByTitleAuthorIsbn.items : [];

    const handleSearchTermChange = (e) => {
        const value = e.currentTarget.value;

        setSearchTerm(value);
        setShowIsbnSearch(false);

        if (value.trim() === '') {
            setShowResults(false);
        }
    }

    useEffect(() => {
        const title = searchTerm.trim();

        if (!title) {
            setShowResults(false);
            return undefined;
        }

        const debounceTimer = setTimeout(() => {
            setShowResults(true);
            searchBooks({
                variables: {
                    title: title,
                    publisherId: selectedSchemeId,
                }
            });
        }, 400);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm, searchBooks, selectedSchemeId]);

    const handleSubmit = (e) => {
        e.preventDefault();
    }

    const handleAddBook = (book) => {
        setSelectedBook({ open: true, book: book });
        setReadingBook({
            bookType: null,
            hideComments: true
        });
    }

    const handleSelectScheme = (scheme) => {
        setShowIsbnSearch(false);
        setSelectedScheme(scheme);
        setSearchTerm('');
        setShowResults(false);
    }

    const handleBackToRecommendations = () => {
        setShowIsbnSearch(false);
        setSelectedScheme(null);
        setSearchTerm('');
        setShowResults(false);
    }

    const handleConfirmAddBook = (book) => {
        assignReadingBook({
            variables: {
                studentId: studentId,
                bookId: book.id,
                bookType: readingBook.bookType,
                hideComments: !readingBook.hideComments,
            }
        });
    }

    const closeSelectedBook = () => {
        setSelectedBook({ open: false, book: null });
    }

    const renderAddBookForm = (book) => {
        return (
            <>
                <div className={cx(styles.addBookHeader, 'u-m-base-20')}>
                    <div>
                        <h1 className={styles.bookHeading}>{book.title}</h1>
                        <h2 className={styles.bookAuthor}>{book.author}</h2>
                        <div className={styles.resultBookCode}>ISBN: {book.isbn ? book.isbn : 'NA'}</div>
                        <div className={cx(styles.resultBookCode, 'u-m-base-5')}>ISBN13: {book.isbn13 ? book.isbn13 : 'NA'}</div>
                    </div>
                </div>
                <div className={styles.addBookField}>
                    <p className="u-m-right-5">{t('book_type')}</p>
                    <ul className={styles.addBookSelectType}>
                        {bookTypes.map(bookType => (
                            <li key={`type${bookType.type}`} className={styles.addBookSelectTypeItem}>
                                <button
                                    type="button"
                                    className={cx(styles.addBookSelectTypeOpt, { [styles.addBookSelectTypeOptSelected]: readingBook.bookType === bookType.type })}
                                    onClick={() => setReadingBook({ ...readingBook, bookType: bookType.type })}
                                >
                                    {t(bookType.label)}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                <p className="u-m-base-5">{t('pupil_comments_enabled')}</p>
                <input type="checkbox" className="switchToggle" id="RecommendationCommentsSwitch" onChange={() => setReadingBook({ ...readingBook, hideComments: !readingBook.hideComments })} checked={readingBook.hideComments} value={readingBook.hideComments}/>
                <label className={styles.switchLabel} htmlFor="RecommendationCommentsSwitch">{t('toggle')}</label>

                <Button disabled={!readingBook.bookType} className="u-m-top-20" onClick={() => handleConfirmAddBook(book)}>
                    {t('add_book')}
                </Button>
            </>
        )
    }

    const searchPlaceholder = selectedScheme ? `Search ${selectedScheme.name} Books` : 'Search by title, author, ISBN or reading scheme';
    const selectedSchemeBooks = selectedScheme && selectedScheme.readingBooks ? selectedScheme.readingBooks : [];

    const renderSearchForm = () => {
        return (
            <form onSubmit={handleSubmit} className={styles.recommendationSearchForm}>
                <label className={styles.recommendationSearchInputWrap}>
                    <Icon.Search size="24" className={styles.recommendationSearchIcon} />
                    <input
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={handleSearchTermChange}
                        className={styles.recommendationSearchInput}
                    />
                </label>
                <button type="submit" className={styles.recommendationSearchButton}>
                    <Icon.Search size="24" />
                    <span>{t('button.search')}</span>
                </button>
                <button
                    type="button"
                    className={styles.recommendationScanButton}
                    onClick={() => {
                        setShowIsbnSearch(true);
                        setShowResults(false);
                    }}
                >
                    <Icon.Camera size="22" />
                    <span>Scan ISBN</span>
                </button>
            </form>
        )
    }

    if (selectedScheme) {
        return (
            <div className={cx(styles.searchMain, styles.recommendationSearchMain)}>
                <div className={styles.schemePageHeader}>
                    <button type="button" className={styles.schemeBackButton} onClick={handleBackToRecommendations}>
                        <Icon.ArrowLeft size="30" />
                        <span>Back</span>
                    </button>
                    <div className={styles.schemePageTitle}>
                        <img src={selectedScheme.iconUrl} alt="" className={styles.schemePageIcon} />
                        <span>{selectedScheme.name}</span>
                    </div>
                </div>

                <section className={cx(styles.recommendationSearchPanel, styles.schemeSearchPanel)}>
                    {renderSearchForm()}
                </section>

                {showIsbnSearch &&
                    <IsbnSearch
                        studentId={studentId}
                        close={close}
                        hide={() => setShowIsbnSearch(false)}
                        reset={reset}
                    />
                }

                {!showIsbnSearch && (showResults ?
                    <div className={styles.recommendationGrid}>
                        {searchLoading && <p className="altDesc">{t('loading')}</p>}
                        {searchError && <p className="altDesc">{searchError.message}</p>}
                        {!searchLoading && !searchError && searchResults.length === 0 && <p className="altDesc">{t('no_matching_books_found')}</p>}
                        {searchResults.map((book, index) => (
                            <BookRecommendationItem
                                book={{
                                    ...book,
                                    coverUrl: book.coverUrl || fallbackCovers[index % fallbackCovers.length]
                                }}
                                onAddBook={handleAddBook}
                                key={book.id}
                            />
                        ))}
                    </div> :
                    <div className={styles.recommendationGrid}>
                        {selectedSchemeBooks.map((book, index) => (
                            <BookRecommendationItem
                                book={{
                                    ...book,
                                    author: book.author || selectedScheme.name,
                                    coverUrl: book.coverUrl || fallbackCovers[index % fallbackCovers.length]
                                }}
                                onAddBook={handleAddBook}
                                key={book.id}
                            />
                        ))}
                    </div>
                )}

                <div className={cx(styles.bookModal, selectedBook.open && styles.bookModalOpen)}>
                    {selectedBook.book && renderAddBookForm(selectedBook.book)}
                </div>
                {selectedBook.open && <Icon.XCircle onClick={closeSelectedBook} className={cx(styles.closeBook, styles.closeBookAlt)}/> }
            </div>
        )
    }

    return (
        <div className={cx(styles.searchMain, styles.recommendationSearchMain)}>
            {close && <Icon.XCircle className={cx(styles.closeSearch, styles.recommendationClose)} onClick={() => close(false)}/>}
            <h2 className={styles.recommendationTitle}>Add a book for {studentName}</h2>
            <p className={styles.recommendationIntro}>Search, scan or browse to quickly add a book to their reading record</p>

            <section className={styles.recommendationSearchPanel}>
                {renderSearchForm()}

                {showIsbnSearch &&
                    <IsbnSearch
                        studentId={studentId}
                        close={close}
                        hide={() => setShowIsbnSearch(false)}
                        reset={reset}
                    />
                }

                {!showIsbnSearch && showResults &&
                    <div className={styles.recommendationResults}>
                        {searchLoading && <p className="altDesc">{t('loading')}</p>}
                        {searchError && <p className="altDesc">{searchError.message}</p>}
                        {!searchLoading && !searchError && searchResults.length === 0 && <p className="altDesc">{t('no_matching_books_found')}</p>}
                        {searchResults.map((book, index) => (
                            <BookRecommendationItem
                                book={{
                                    ...book,
                                    coverUrl: book.coverUrl || fallbackCovers[index % fallbackCovers.length]
                                }}
                                variant="result"
                                onAddBook={handleAddBook}
                                key={book.id}
                            />
                        ))}
                    </div>
                }
            </section>

            {!showIsbnSearch && <>
                <h3 className={styles.recommendationSectionTitle}>Browse reading schemes</h3>
                {publishersLoading && <p className="altDesc">{t('loading')}</p>}
                {publishersError && <p className="altDesc">{publishersError.message}</p>}
                {!publishersLoading && !publishersError && <ReadingSchemeCarousel schemes={readingSchemes} onSelectScheme={handleSelectScheme} />}

                {showRecommendedBooks && <>
                    <h3 className={styles.recommendationSectionTitle}>Recommended Books for {studentName}</h3>
                    <div className={styles.recommendationGrid}>
                        {recommendedLoading && <p className="altDesc">{t('loading')}</p>}
                        {recommendedError && <p className="altDesc">{recommendedError.message}</p>}
                        {!recommendedLoading && !recommendedError && recommendedBooks.length === 0 && <p className="altDesc">{t('no_matching_books_found')}</p>}
                        {recommendedBooks.map((book, index) => (
                            <BookRecommendationItem
                                book={{
                                    ...book,
                                    coverUrl: book.coverUrl || fallbackCovers[index % fallbackCovers.length]
                                }}
                                onAddBook={handleAddBook}
                                key={book.id}
                            />
                        ))}
                    </div>
                </>}
                <div className={cx(styles.bookModal, selectedBook.open && styles.bookModalOpen)}>
                    {selectedBook.book && renderAddBookForm(selectedBook.book)}
                </div>
                {selectedBook.open && <Icon.XCircle onClick={closeSelectedBook} className={cx(styles.closeBook, styles.closeBookAlt)}/> }
            </>}
        </div>
    )
}

export default BookRecommendationSearch;
